import { getEnv } from '@/constants';
import { GCAUTH_ACCESS_TOKEN_KEY } from '@/services/gcauth.service';
import loggerService from '@/services/logger.service';

interface RouteLike {
  query?: Record<string, unknown>;
}

function isLocalDev(): boolean {
  return import.meta.env.MODE === 'development';
}

function getVanityOnDemandAppOrigin(currentEvent: any, org: any): string {
  if (isLocalDev()) {
    return getEnv('VUE_APP_LOCAL_ONDEMAND_APP_ORIGIN');
  }

  return currentEvent?.on_demand_app_url
    ? new URL(currentEvent.on_demand_app_url || '')?.origin
    : `https://${org?.short_name}${getEnv('VUE_APP_ONDEMAND_PAGE_URL')}`;
}

function getCurrentAccessToken(store: any): string | null {
  return (
    store?.getters?.userAuthToken || store?.state?.user?.accessToken || localStorage.getItem(GCAUTH_ACCESS_TOKEN_KEY)
  );
}

function decodeSessionIdFromToken(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    const decoded = JSON.parse(atob(payload));
    return decoded?.session_id || null;
  } catch (error: any) {
    loggerService.warn('Failed to decode session id from access token', {
      error: error?.message || error
    });
    return null;
  }
}

function appendSessionIdToUrl(url: string, store: any): string {
  const accessToken = getCurrentAccessToken(store);
  if (!accessToken) {
    return url;
  }

  const sessionId = decodeSessionIdFromToken(accessToken);
  if (!sessionId) {
    return url;
  }

  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.set('session_id', sessionId);
    return parsedUrl.toString();
  } catch (error: any) {
    loggerService.warn('Failed to append session id to on-demand redirect URL', {
      error: error?.message || error
    });
    return url;
  }
}

export function isOnDemandWebinar(currentEvent: any): boolean {
  return !!(currentEvent?.event_type === 'Webinar' && currentEvent?.on_demand_mode);
}

export function getOnDemandRedirectUrl({ store, route }: { store: any; route: RouteLike }): string | null {
  const currentEvent = store.getters['event/currentEvent'];
  if (!isOnDemandWebinar(currentEvent)) {
    return null;
  }

  const org = store.getters['event/currentOrganization'];
  const vanityOnDemandAppOrigin = getVanityOnDemandAppOrigin(currentEvent, org);

  const isComingFromEventAppMagicLink = window.location.pathname.includes('/auth/link/');
  const isEmbeddedApp = route?.query?.isEmbedded === 'true';

  let onDemandPageMagicLink = `${vanityOnDemandAppOrigin}/on-demand/${route?.query?.eventID}`;

  if (isComingFromEventAppMagicLink) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('on_demand_app', 'true');
    onDemandPageMagicLink = `${vanityOnDemandAppOrigin}${window.location.pathname}?${searchParams.toString()}`;
  }

  if (isEmbeddedApp) {
    onDemandPageMagicLink = `https://${org?.short_name}${getEnv('VUE_APP_ONDEMAND_PAGE_URL')}/auth/link/${
      route?.query?.emailId
    }/${route?.query?.secretCode}${window.location.search}`;
  }

  return appendSessionIdToUrl(onDemandPageMagicLink, store);
}

export function redirectToOnDemandExperienceIfApplicable({ store, route }: { store: any; route: RouteLike }): boolean {
  const url = getOnDemandRedirectUrl({ store, route });
  if (!url) {
    return false;
  }
  window.location.href = url;
  return true;
}
